import React, { useState } from 'react';
import { TrendingUp, TrendingDown, Minus, Zap, Info, ChevronDown, ChevronUp, ChevronRight } from 'lucide-react';
import { FaFacebook, FaInstagram, FaApple, FaGoogle, FaTiktok, FaYoutube, FaLinkedin, FaTwitter, FaMapMarkerAlt, FaUserTie, FaSms, FaRobot, FaHome, FaReddit, FaPinterest, FaEnvelope } from 'react-icons/fa';
import SourceDetailModal from './SourceDetailModal';

const METRIC_GLOSSARY = [
    {
        label: 'Visitors',
        body: 'Unique browsing sessions on the Buyer Portal from this source — counts both anonymous visitors and logged-in buyers. One person visiting twice in separate sessions counts as 2.',
    },
    {
        label: 'Signups',
        body: 'Count of new buyer accounts attributed to this source (from signup UTM tags stored on the account). Matches the Signups KPI card.',
    },
    {
        label: 'Conv. Rate',
        body: 'Signups ÷ Visitors × 100. Measures how effectively a source turns traffic into account creations.',
    },
    {
        label: 'Enquiries',
        body: 'Enquiries sent by buyers from this source in the selected range — the mid-funnel signal between a signup and a sale.',
    },
    {
        label: 'Sales',
        body: 'Portal sales attributed to this source: a sold listing whose accepted-offer buyer first arrived via this source.',
    },
    {
        label: 'Commission',
        body: 'Collings commission earned on those attributed sales. This is the true value of a source — where the money actually comes from.',
    },
];

const formatCurrency = (value) => {
    const n = Number(value) || 0;
    if (n === 0) return '$0';
    if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `$${Math.round(n / 1000)}k`;
    return `$${Math.round(n).toLocaleString()}`;
};

// `style` is the icon-tile background (Instagram is a gradient); `accent` is a
// solid hex used by the drill-down panel for bars/tints (gradients break there).
const PLATFORM_CONFIG = {
    fb:        { label: 'Facebook',  Icon: FaFacebook,  style: { background: '#1877F2' }, accent: '#1877F2' },
    facebook:  { label: 'Facebook',  Icon: FaFacebook,  style: { background: '#1877F2' }, accent: '#1877F2' },
    // Facebook (Meta) paid ads — own row, sky tint to read apart from organic Facebook.
    facebook_ads: { label: 'Facebook Ads', Icon: FaFacebook, style: { background: '#0EA5E9' }, accent: '#0EA5E9' },
    // ChatGPT (OpenAI) paid ads — canonical row was previously unstyled here.
    chatgpt_ads:  { label: 'ChatGPT Ads',  Icon: FaRobot,    style: { background: '#6366F1' }, accent: '#6366F1' },
    ig:        { label: 'Instagram', Icon: FaInstagram, style: { background: 'linear-gradient(135deg, #f58529, #dd2a7b, #8134af)' }, accent: '#dd2a7b' },
    instagram: { label: 'Instagram', Icon: FaInstagram, style: { background: 'linear-gradient(135deg, #f58529, #dd2a7b, #8134af)' }, accent: '#dd2a7b' },
    an:        { label: 'Apple Ads', Icon: FaApple,     style: { background: '#1c1c1e' }, accent: '#1c1c1e' },
    apple:     { label: 'Apple Ads', Icon: FaApple,     style: { background: '#1c1c1e' }, accent: '#1c1c1e' },
    google:    { label: 'Google',    Icon: FaGoogle,    style: { background: '#4285F4' }, accent: '#4285F4' },
    g:         { label: 'Google',    Icon: FaGoogle,    style: { background: '#4285F4' }, accent: '#4285F4' },
    tiktok:    { label: 'TikTok',    Icon: FaTiktok,    style: { background: '#010101' }, accent: '#010101' },
    youtube:   { label: 'YouTube',   Icon: FaYoutube,   style: { background: '#FF0000' }, accent: '#FF0000' },
    linkedin:  { label: 'LinkedIn',  Icon: FaLinkedin,  style: { background: '#0A66C2' }, accent: '#0A66C2' },
    twitter:   { label: 'Twitter',   Icon: FaTwitter,   style: { background: '#1DA1F2' }, accent: '#1DA1F2' },
    geo_seo:   { label: 'Geo SEO',   Icon: FaMapMarkerAlt, style: { background: '#0E9F6E' }, accent: '#0E9F6E' },
    local_seo: { label: 'Geo SEO',   Icon: FaMapMarkerAlt, style: { background: '#0E9F6E' }, accent: '#0E9F6E' },
    traditional_seo: { label: 'Traditional SEO', Icon: FaGoogle, style: { background: '#4285F4' }, accent: '#4285F4' },
    real_estate_agent: { label: 'Real Estate Agents', Icon: FaUserTie, style: { background: '#7C3AED' }, accent: '#7C3AED' },
    sms:       { label: 'SMS',       Icon: FaSms, style: { background: '#0D9488' }, accent: '#0D9488' },
    direct:    { label: 'Direct',    Icon: null, style: { background: '#55C5D0' }, accent: '#55C5D0' },
    // Channels the portal now infers when an ad's UTM is lost on the way in.
    // 'website' = came through the Collings marketing site (the ad tag was
    // dropped at that hop), which used to be recorded as no source at all.
    website:   { label: 'Collings Website', Icon: FaHome, style: { background: '#0891B2' }, accent: '#0891B2' },
    collings:  { label: 'Collings Website', Icon: FaHome, style: { background: '#0891B2' }, accent: '#0891B2' },
    bing:      { label: 'Bing',      Icon: null, style: { background: '#F59E0B' }, accent: '#F59E0B' },
    duckduckgo:{ label: 'DuckDuckGo', Icon: null, style: { background: '#DE5833' }, accent: '#DE5833' },
    yahoo:     { label: 'Yahoo',     Icon: null, style: { background: '#6001D2' }, accent: '#6001D2' },
    // Organic ChatGPT referral, kept apart from the paid ChatGPT Ads row.
    chatgpt_referral: { label: 'ChatGPT (Referral)', Icon: FaRobot, style: { background: '#10A37F' }, accent: '#10A37F' },
    perplexity: { label: 'Perplexity', Icon: FaRobot, style: { background: '#20808D' }, accent: '#20808D' },
    gemini:    { label: 'Gemini',    Icon: FaRobot, style: { background: '#8E7CFF' }, accent: '#8E7CFF' },
    reddit:    { label: 'Reddit',    Icon: FaReddit, style: { background: '#FF4500' }, accent: '#FF4500' },
    pinterest: { label: 'Pinterest', Icon: FaPinterest, style: { background: '#E60023' }, accent: '#E60023' },
    realestate_com_au: { label: 'realestate.com.au', Icon: FaHome, style: { background: '#E4002B' }, accent: '#E4002B' },
    domain_com_au: { label: 'Domain', Icon: FaHome, style: { background: '#0E9F6E' }, accent: '#0E9F6E' },
    email:     { label: 'Email',     Icon: FaEnvelope, style: { background: '#7C3AED' }, accent: '#7C3AED' },
    // Sign-ups whose first touch was never captured (mostly accounts created
    // before 15 Sep). Deliberately NOT folded into Direct: "Direct" means they
    // came straight to us, "Unknown" means we have nothing.
    unknown:   { label: 'Unknown',   Icon: null, style: { background: '#9CA3AF' }, accent: '#9CA3AF' },
    // Unrecognised low-volume tags (typos, truncated links) rolled into one row.
    other:     { label: 'Other',     Icon: null, style: { background: '#9CA3AF' }, accent: '#9CA3AF' },
};

const getPlatform = (source) => {
    const key = source?.toLowerCase();
    return PLATFORM_CONFIG[key] || { label: source || '?', Icon: null, style: { background: '#55C5D0' }, accent: '#55C5D0' };
};

const MEDIUM_LABELS = {
    paid:    { label: 'Paid',    bg: 'bg-orange-100', text: 'text-orange-700' },
    organic: { label: 'Organic', bg: 'bg-green-100',  text: 'text-green-700' },
    cpc:     { label: 'CPC',     bg: 'bg-purple-100', text: 'text-purple-700' },
    social:  { label: 'Social',  bg: 'bg-blue-100',   text: 'text-blue-700' },
    email:   { label: 'Email',   bg: 'bg-teal-100',   text: 'text-teal-700' },
    sms:     { label: 'SMS',     bg: 'bg-teal-100',   text: 'text-teal-700' },
};

// Our own outbound modules arrive as the utm_medium. Each used to be its own
// "Email" row in the table; they are now the breakdown inside the Email row, so
// they need names a person would recognise.
const MODULE_LABELS = {
    new_listing_general: 'New listing alert',
    new_listing_matched: 'Matched listing alert',
    weekly_digest: 'Weekly digest',
    portal_invite: 'Portal invite',
    enquiry_auto: 'Enquiry auto-reply',
    find_buyers: 'Find Buyers send',
    campaign: 'Campaign',
    campaign_test: 'Campaign test send',
    open_home_email: 'Open home email',
    agent_qr: 'Agent QR code',
    referral: 'Referral',
    none: 'No medium',
};

const prettyMedium = (medium) => {
    if (!medium) return 'Not tagged';
    const key = medium.toLowerCase();
    if (MODULE_LABELS[key]) return MODULE_LABELS[key];
    if (MEDIUM_LABELS[key]) return MEDIUM_LABELS[key].label;
    return medium.replace(/_/g, ' ');
};

const getMedium = (medium) => {
    const key = medium?.toLowerCase();
    return MEDIUM_LABELS[key] || { label: prettyMedium(medium), bg: 'bg-gray-100', text: 'text-gray-600' };
};

const ConvBadge = ({ rate }) => {
    if (rate >= 10) return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
            <TrendingUp className="w-3 h-3" />{rate}%
        </span>
    );
    if (rate >= 5) return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
            <TrendingUp className="w-3 h-3" />{rate}%
        </span>
    );
    if (rate > 0) return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
            <TrendingDown className="w-3 h-3" />{rate}%
        </span>
    );
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-400">
            <Minus className="w-3 h-3" />0%
        </span>
    );
};

const MetricCell = ({ value, highlight = false }) => {
    const isZero = value === 0;
    return (
        <span className={`text-sm font-medium tabular-nums ${
            isZero ? 'text-gray-300' : highlight ? 'text-[#55C5D0]' : 'text-gray-800'
        }`}>
            {value.toLocaleString()}
        </span>
    );
};

const SourceQualityChart = ({ sources, startDate, endDate }) => {
    const [showGlossary, setShowGlossary] = useState(false);
    const [detailSource, setDetailSource] = useState(null);
    // Channels whose medium breakdown is open. A channel is one row now, so the
    // per-medium split (which email, which ad set) lives behind this toggle.
    const [expanded, setExpanded] = useState(() => new Set());

    const toggleExpanded = (key) => setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    // Always surface both SEO channels as drill-in rows — append a zero row for
    // any that the backend didn't return (e.g. Traditional SEO with no traffic yet)
    // so agents can still click through to the detailed dashboard.
    const rows = React.useMemo(() => {
        const list = [...(sources || [])];
        const present = new Set(list.map((s) => s.utm_source?.toLowerCase()));
        const seoDefaults = [
            { utm_source: 'geo_seo', utm_medium: 'organic' },
            { utm_source: 'traditional_seo', utm_medium: 'organic' },
            // Always surface the per-agent QR channel so agents can drill in even
            // before the first attributed sign-up arrives.
            { utm_source: 'real_estate_agent', utm_medium: 'agent_qr' },
        ];
        seoDefaults.forEach((d) => {
            if (!present.has(d.utm_source)) {
                list.push({ ...d, total_users: 0, signups: 0, conversion_rate: 0 });
            }
        });
        return list;
    }, [sources]);

    if (rows.length === 0) {
        return (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                    <Zap className="w-4 h-4 text-[#55C5D0]" />
                    <h3 className="text-sm font-semibold text-gray-900">Traffic Sources</h3>
                </div>
                <p className="text-sm text-gray-400 text-center py-8">No source data available</p>
            </div>
        );
    }

    const totalSignups = rows.reduce((sum, s) => sum + s.signups, 0);
    const totalCommission = rows.reduce((sum, s) => sum + (Number(s.revenue_commission) || 0), 0);
    const totalSales = rows.reduce((sum, s) => sum + (Number(s.attributed_sales) || 0), 0);

    return (
        <>
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-[#55C5D0]" />
                    <h3 className="text-sm font-semibold text-gray-900">Traffic Sources</h3>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => setShowGlossary(v => !v)}
                        className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-[#55C5D0] transition-colors px-2 py-1 rounded-md hover:bg-gray-50"
                        aria-expanded={showGlossary}
                    >
                        <Info className="w-3.5 h-3.5" />
                        What do these mean?
                        {showGlossary ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </button>
                    <div className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-3 py-1.5">
                        <span className="text-xs text-gray-500">Total signups</span>
                        <span className={`text-sm font-bold ${totalSignups > 0 ? 'text-[#55C5D0]' : 'text-gray-300'}`}>
                            {totalSignups}
                        </span>
                    </div>
                    <div className="flex items-center gap-1.5 bg-emerald-50 rounded-lg px-3 py-1.5">
                        <span className="text-xs text-emerald-600">Attributed commission</span>
                        <span className={`text-sm font-bold ${totalCommission > 0 ? 'text-emerald-700' : 'text-gray-300'}`}>
                            {formatCurrency(totalCommission)}
                        </span>
                        {totalSales > 0 && (
                            <span className="text-[10px] text-emerald-500 font-medium">
                                ({totalSales} {totalSales === 1 ? 'sale' : 'sales'})
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Glossary */}
            {showGlossary && (
                <div className="px-5 py-4 bg-gray-50/70 border-b border-gray-100">
                    <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                        {METRIC_GLOSSARY.map((item) => (
                            <div key={item.label} className="flex gap-2">
                                <dt className="text-[11px] font-semibold text-gray-800 uppercase tracking-wider min-w-[80px] flex-shrink-0">
                                    {item.label}
                                </dt>
                                <dd className="text-[11px] text-gray-600 leading-relaxed">{item.body}</dd>
                            </div>
                        ))}
                    </dl>
                </div>
            )}

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-gray-100">
                            <th className="px-5 py-3 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-widest w-52">
                                Source / Medium
                            </th>
                            <th className="px-4 py-3 text-right text-[10px] font-semibold text-gray-400 uppercase tracking-widest">
                                Visitors
                            </th>
                            <th className="px-4 py-3 text-right text-[10px] font-semibold text-[#55C5D0] uppercase tracking-widest">
                                Signups
                            </th>
                            <th className="px-4 py-3 text-right text-[10px] font-semibold text-gray-400 uppercase tracking-widest">
                                Conv. Rate
                            </th>
                            <th className="px-4 py-3 text-right text-[10px] font-semibold text-gray-400 uppercase tracking-widest">
                                Enquiries
                            </th>
                            <th className="px-4 py-3 text-right text-[10px] font-semibold text-gray-400 uppercase tracking-widest">
                                Sales
                            </th>
                            <th className="px-4 py-3 text-right text-[10px] font-semibold text-emerald-600 uppercase tracking-widest">
                                Commission
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                        {rows.map((source, index) => {
                            const platform = getPlatform(source.utm_source);
                            const medium = source.utm_medium ? getMedium(source.utm_medium) : null;
                            const mediums = source.mediums || [];
                            const isSplit = mediums.length > 1;
                            const rowKey = source.utm_source || `row-${index}`;
                            const isOpen = expanded.has(rowKey);

                            const openDetail = () => setDetailSource({
                                utm_source: source.utm_source,
                                utm_medium: source.utm_medium,
                                label: platform.label,
                                accent: platform.accent,
                                style: platform.style,
                                Icon: platform.Icon,
                            });

                            return (
                                <React.Fragment key={rowKey}>
                                <tr
                                    onClick={openDetail}
                                    className="cursor-pointer transition-colors group hover:bg-gray-50/60"
                                >
                                    {/* Source / Medium */}
                                    <td className="px-5 py-3.5">
                                        <div className="flex items-center gap-2.5">
                                            <span
                                                className="inline-flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 text-white"
                                                style={platform.style}
                                            >
                                                {platform.Icon
                                                    ? <platform.Icon size={16} />
                                                    : <span className="text-[10px] font-bold">{source.utm_source?.slice(0, 2).toUpperCase()}</span>
                                                }
                                            </span>
                                            <div>
                                                <div className="text-xs font-semibold text-gray-800 leading-tight flex items-center gap-1 whitespace-nowrap">
                                                    {platform.label}
                                                    <span className="hidden group-hover:inline-flex items-center gap-0.5 text-[10px] font-medium text-[#55C5D0]">
                                                        View detail<ChevronRight className="w-3 h-3" />
                                                    </span>
                                                </div>
                                                {medium && !isSplit && (
                                                    <span className={`inline-block mt-0.5 px-1.5 py-0 rounded text-[10px] font-medium ${medium.bg} ${medium.text}`}>
                                                        {medium.label}
                                                    </span>
                                                )}
                                                {isSplit && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); toggleExpanded(rowKey); }}
                                                        aria-expanded={isOpen}
                                                        className="inline-flex items-center gap-0.5 mt-0.5 px-1.5 py-0 rounded text-[10px] font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                                                    >
                                                        {mediums.length} mediums
                                                        {isOpen ? <ChevronUp className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </td>

                                    {/* Users */}
                                    <td className="px-4 py-3.5 text-right">
                                        <MetricCell value={source.total_users} />
                                    </td>

                                    {/* Signups */}
                                    <td className="px-4 py-3.5 text-right">
                                        <MetricCell value={source.signups} highlight />
                                    </td>

                                    {/* Conv Rate */}
                                    <td className="px-4 py-3.5 text-right">
                                        <ConvBadge rate={source.conversion_rate} />
                                    </td>

                                    {/* Enquiries */}
                                    <td className="px-4 py-3.5 text-right">
                                        <MetricCell value={source.enquiries || 0} />
                                    </td>

                                    {/* Sales */}
                                    <td className="px-4 py-3.5 text-right">
                                        <MetricCell value={source.attributed_sales || 0} />
                                    </td>

                                    {/* Commission */}
                                    <td className="px-4 py-3.5 text-right">
                                        <span className={`text-sm font-semibold tabular-nums ${
                                            (Number(source.revenue_commission) || 0) > 0 ? 'text-emerald-700' : 'text-gray-300'
                                        }`}>
                                            {formatCurrency(source.revenue_commission)}
                                        </span>
                                    </td>
                                </tr>

                                {/* Medium breakdown: the rows this channel used
                                    to be split into, kept as detail instead. */}
                                {isSplit && isOpen && mediums.map((m) => (
                                    <tr key={`${rowKey}-${m.utm_medium || 'none'}`} className="bg-gray-50/50">
                                        <td className="pl-16 pr-5 py-2">
                                            <span className="text-[11px] text-gray-500">{prettyMedium(m.utm_medium)}</span>
                                        </td>
                                        <td className="px-4 py-2 text-right">
                                            <span className="text-[11px] text-gray-500 tabular-nums">{(m.total_users || 0).toLocaleString()}</span>
                                        </td>
                                        <td className="px-4 py-2 text-right">
                                            <span className="text-[11px] text-gray-500 tabular-nums">{(m.signups || 0).toLocaleString()}</span>
                                        </td>
                                        <td className="px-4 py-2 text-right">
                                            <span className="text-[11px] text-gray-500 tabular-nums">{m.conversion_rate || 0}%</span>
                                        </td>
                                        <td className="px-4 py-2 text-right">
                                            <span className="text-[11px] text-gray-500 tabular-nums">{(m.enquiries || 0).toLocaleString()}</span>
                                        </td>
                                        <td className="px-4 py-2" />
                                        <td className="px-4 py-2" />
                                    </tr>
                                ))}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Footer note */}
            <div className="px-5 py-3 bg-gray-50 border-t border-gray-100">
                <p className="text-[10px] text-gray-400">
                    Conv. rate = signups ÷ visitors. Sales & commission are portal sales attributed to the
                    source of the accepted-offer buyer's first visit. Click any row for the full channel
                    breakdown, or "What do these mean?" above for column definitions.
                </p>
            </div>
        </div>

        <SourceDetailModal
            isOpen={!!detailSource}
            source={detailSource}
            startDate={startDate}
            endDate={endDate}
            onClose={() => setDetailSource(null)}
        />
        </>
    );
};

export default SourceQualityChart;
